import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KButton, KSpan, KTooltip, KDropdown } from "kahuna-base-react-components"
import { handleAuthorizedRequest, summaries, tracks, users } from "../../../../../apis"
import { getCurrency, mapListToCamelCase, mapToCamelCase } from "../../../../../utility"
import { BaseSummary, SummaryDuplicate, User } from "../../../../../types"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
//@ts-ignore
import CheckIcon from "../../../../../assets/admin_icons/check-circle.svg"
//@ts-ignore
import CloseIcon from "../../../../../assets/admin_icons/close-circle.svg"

type RoyaltySummaryDuplicateProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const RoyaltySummaryDuplicate: FC<RoyaltySummaryDuplicateProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [summaryDuplicate, setSummaryDuplicate] = useState<SummaryDuplicate>()
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()
  const [searchUserOptions, setSearchUserOptions] = useState<KSelectOption[]>([])
  const [searchText, setSearchText] = useState<string>("")
  const [timeoutId, setTimeoutId] = useState(null)

  const fetchRoyaltySummaryDuplicates = async () => {
    setLoading(true)
    const userIdParam = selectedUser ? `?user_id=${selectedUser.value}` : ""
    summaries(`/summary_duplicate_list/${userIdParam}`, "GET").then((resp) => {
      if (resp.data) {
        const data: SummaryDuplicate = mapToCamelCase(resp.data)
        setSummaryDuplicate(data)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchRoyaltySummaryDuplicates()
  }, [])

  const onSearchTextChanged = async (text: string) => {
    setSearchText(text)
    if (!text) return
    setSearchUserOptions([])
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setTimeoutId(
      //@ts-ignore
      setTimeout(async () => {
        await handleAuthorizedRequest(async () => {
          const response = await users(`/?limit=10&offset=0&username=${text}`, "get")
          setSearchUserOptions(
            response.data.results.map((user: User) => ({
              value: user.id,
              label: user.username
            }))
          )
        }, "")
      }, 500)
    )
  }

  const zeroOutSummary = (summary: BaseSummary) => {
    setLoading(true)
    if (loading) {
      return
    }
    if (summary.paymentApproved) {
      toast.error("Cannot zero out a summary with approved payment.", errorFilledToastOptions)
      return
    }
    summaries(`/zero_out_summary_duplicate/?id=${summary.id}`, "PUT")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Success.", successFilledToastOptions)
          fetchRoyaltySummaryDuplicates()
        } else {
          setLoading(false)
          toast.error("Error.", errorFilledToastOptions)
        }
      })
      .catch(() => {
        setLoading(false)
        toast.error("Error.", errorFilledToastOptions)
      })
  }

  const brokeSummaryDuplicate = (summary: BaseSummary) => {
    setLoading(true)
    if (loading) {
      return
    }
    summaries(`/broke_summary_duplicate/?id=${summary.id}`, "PUT")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Success.", successFilledToastOptions)
          fetchRoyaltySummaryDuplicates()
        } else {
          toast.error("Error.", errorFilledToastOptions)
          setLoading(false)
        }
      })
      .catch(() => {
        toast.error("Error.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const renderRoyaltySummaryDuplicates = () => {
    if (!summaryDuplicate || !summaryDuplicate.summaryList?.length) {
      return (
        <div className="mt-6">
          <KSpan text="No summary duplicate found." fontSize={16} lineHeight="24px" />
        </div>
      )
    }

    return (
      <div className="mt-6 flex flex-col gap-4">
        {/* User info */}
        <div className="mb-2">
          <KTitleSpan fontSize={16} lineHeight="22px" text={`User: ${summaryDuplicate.user.username}`} />
        </div>

        {/* Header */}
        <div className="grid grid-cols-9 gap-4 px-4 py-2 bg-gray-100 rounded">
          <KSpan text="Summary ID" />
          <KSpan text="Date" />
          <KSpan text="Transfer Date" />
          <KSpan text="Platform" />
          <KSpan text="Total Amount" />
          <KSpan text="Total Net" />
          <KSpan text="Approved" />
          <KSpan text="Payment Approved" />
          <KSpan text="Action" />
        </div>

        {/* Rows */}
        {summaryDuplicate.summaryList.map((item: BaseSummary) => {
          return (
            <div key={item.id} className="grid grid-cols-9 gap-4 px-4 py-3 border rounded items-center">
              <KSpan text={`#${item.id}`} />
              <KSpan text={item.date} />
              <KSpan text={item.transferDate} />
              <KSpan text={item.value} />
              <KSpan text={`${item.totalAmount.toFixed(7)} ${getCurrency(item.currency)}`} />
              <KSpan text={`${item.totalNet.toFixed(7)} ${getCurrency(item.currency)}`} />
              <img src={item.approved ? CheckIcon : CloseIcon} />
              <img src={item.paymentApproved ? CheckIcon : CloseIcon} />

              <div className="flex gap-2">
                <KButton
                  text="Zero out"
                  background="red"
                  textColor="white"
                  width="110px"
                  onClick={() => zeroOutSummary(item)}
                />
                <KButton
                  text="Skip"
                  background="transparent"
                  width="110px"
                  onClick={() => brokeSummaryDuplicate(item)}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"Royalty Summary Duplicate"} />
          </div>
        </KTooltip>
        <div className="flex gap-4">
          <KDropdown
            dataTestId="reports-approval-search-filter"
            placeholder={"Search user to filter"}
            padding="8px"
            width={240}
            selected={selectedUser}
            onInputChange={onSearchTextChanged}
            options={searchUserOptions}
            isClearable={true}
            onSelect={(selectedOption) => {
              const selected = selectedOption as KSelectOption
              setSelectedUser(selected)
            }}
          />
          <KButton
            text="Apply"
            onClick={() => fetchRoyaltySummaryDuplicates()}
            background="black"
            textColor="white"
            width="120px"
          />
        </div>
      </div>

      {renderRoyaltySummaryDuplicates()}
    </div>
  )
}

export default RoyaltySummaryDuplicate
